import { observable, runInAction } from "mobx";
import { atom } from "../atom";
import { STATE_PANE_GETTER_OUTPUT_INDEX } from "../Constants";
import { uiStateMobx } from "../SharedIframeState";
import {
  Action,
  ActionType,
  PaneInputGlobalKey,
  PaneType,
  Position,
} from "../Types";
import { assert, generatePaneId, generatePaneInputId } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  canvasAtom,
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
} from "./CanvasState";
import { setLogInspectorValues } from "./LogInspector";
import {
  convertViewportPositionToCanvasOffset,
  getPaneInputAutoName,
  positionDistance,
  roundPositionToGrid,
  validatePaneInputName,
  VALID_VARIABLE_NAME_REGEX,
} from "./SandboxUtils";

let focusPaneIdOnMount: string | undefined;

export function getFocusPaneIdOnMount() {
  return focusPaneIdOnMount;
}

export function setFocusPaneIdOnMount(value: string | undefined) {
  focusPaneIdOnMount = value;
  if (value !== undefined && focusedPaneIdAtom.value !== value) {
    runInAction(() => {
      focusedPaneIdAtom.value = value;
    });
  }
}

export const ArrayMapSentinel = -1;

type DragValueSettings =
  | {
      type: "reference";
      fromPaneId: string;
      fromPaneOutputIndex: number | undefined;
      sourceInfo:
        | {
            type: "pane-output";
            duplicateInputName?: string;
            hoverPaneId: string | undefined;
            hoverPaneInputKey: PaneInputGlobalKey | undefined;
          }
        | {
            type: "data-column";
            keyPath: (string | number)[];
            columnName: string;
            isPaneValueObjectFormat: boolean;
          }
        | { type: "key-path"; keyPath: (string | number)[] };
    }
  | { type: "value"; name: string | undefined; value: any };
export const dragValueAtom = atom<
  | (DragValueSettings & {
      position: Position;
    })
  | undefined
>(undefined);
export const paneToolDragMobx = observable.box<
  { paneType: PaneType; position: Position; size: Position } | undefined
>(undefined);

export function handleNewPaneCreate(options?: { useKeyAsName?: boolean }) {
  runInAction(() => {
    const dragValue = dragValueAtom.value;
    dragValueAtom.value = undefined;
    uiStateMobx.maximizedPaneId = undefined;
    assert(dragValue !== undefined);
    const paneId = generatePaneId();
    setFocusPaneIdOnMount(paneId);
    if (dragValue.type === "reference") {
      const { sourceInfo } = dragValue;
      assert(
        sourceInfo.type !== "pane-output" ||
          sourceInfo.hoverPaneId === undefined
      );
      // pane name could have changed since mousedown
      const refreshedFromPane = canvasAtom.value.panes.find(
        (p) => p.id === dragValue.fromPaneId
      );
      if (refreshedFromPane === undefined) {
        return;
      }
      const inputPaneName =
        sourceInfo.type === "pane-output" &&
        sourceInfo.duplicateInputName !== undefined
          ? validatePaneInputName(sourceInfo.duplicateInputName, [], undefined)
          : getPaneInputAutoName(
              [],
              dragValue.fromPaneOutputIndex,
              refreshedFromPane,
              undefined
            );
      assert(inputPaneName !== undefined);
      const action: Action = [
        ActionType.CreateTransformPane,
        {
          paneId,
          paneInput: {
            id: generatePaneInputId(),
            name: inputPaneName,
            source: [dragValue.fromPaneId, dragValue.fromPaneOutputIndex ?? 0],
          },
          expression: `${inputPaneName}${
            sourceInfo.type === "data-column" &&
            sourceInfo.isPaneValueObjectFormat
              ? ".data"
              : ""
          }${
            sourceInfo.type !== "pane-output"
              ? sourceInfo.keyPath
                  .map((key) =>
                    typeof key === "string"
                      ? VALID_VARIABLE_NAME_REGEX.test(key)
                        ? `.${key}`
                        : `["${key}"]`
                      : key === ArrayMapSentinel
                      ? ".map(row => row"
                      : `[${key}]`
                  )
                  .join("")
              : ""
          }${
            sourceInfo.type !== "pane-output" && sourceInfo.keyPath.includes(-1)
              ? ")"
              : ""
          }${
            dragValue.fromPaneOutputIndex === STATE_PANE_GETTER_OUTPUT_INDEX
              ? "()"
              : ""
          }`,
          position: dragValue.position,
        },
      ];
      if (options?.useKeyAsName === true) {
        const lastKey =
          sourceInfo.type !== "pane-output"
            ? sourceInfo.keyPath[sourceInfo.keyPath.length - 1]
            : undefined;
        if (typeof lastKey === "string") {
          action[1].name = lastKey;
        }
      }
      canvasDispatch(action);
    } else if (dragValue.type === "value") {
      setLogInspectorValues(paneId, dragValue.value);
      canvasDispatch([
        ActionType.CreatePane,
        {
          paneId,
          type: PaneType.State,
          position: dragValue.position,
          name: dragValue.name,
        },
      ]);
    } else {
      throw new Error();
    }
  });
}

const dragShield = document.createElement("div");
dragShield.className =
  "fixed top-0 left-0 right-0 bottom-0 z-99999 cursor-pointer";

export function onDragNewPaneMouseDown(
  e: {
    button: number;
    clientX: number;
    clientY: number;
    preventDefault: () => void;
  },
  updateDragNewPaneStore: (position: Position) => void,
  cleanupRef: React.MutableRefObject<(() => void) | undefined>,
  onCreate: () => void,
  createWithoutMouseMove: boolean = false
): void {
  if (e.button !== 0) {
    return;
  }
  let didMoveMouse = false;
  const startMousePosition: Position = [e.clientX, e.clientY];
  function update(e: { clientX: number; clientY: number }) {
    updateDragNewPaneStore(
      roundPositionToGrid(
        convertViewportPositionToCanvasOffset(
          [e.clientX, e.clientY],
          canvasTransformComputed.get(),
          canvasViewport.zoom
        ),
        1
      )
    );
  }
  if (createWithoutMouseMove) {
    update(e);
  }
  function onMouseMove(e: MouseEvent) {
    if (!didMoveMouse) {
      if (positionDistance(startMousePosition, [e.clientX, e.clientY]) < 3) {
        return;
      }
      document.body.appendChild(dragShield);
      didMoveMouse = true;
    }
    update(e);
  }
  function cleanup() {
    if (didMoveMouse) {
      document.body.removeChild(dragShield);
    }
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    cleanupRef.current = undefined;
  }
  function onMouseUp(e: MouseEvent) {
    if (didMoveMouse || createWithoutMouseMove) {
      handleNewPaneCreate({
        useKeyAsName: e.ctrlKey,
      });
      onCreate();
    }
    cleanup();
  }
  cleanupRef.current = cleanup;
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
  e.preventDefault();
}
